/**
 * KraftStories — BOOKING JOURNEY
 * ============================================================================
 * The one blind step between "clicked Book Now" and "added to cart".
 *
 * WHAT THIS IS FOR
 * ----------------
 * Over 14 days: book_bow_button_click 131 users, add_to_cart 137 users. Between
 * those two numbers sits Cowlendar's date-and-time picker, and modal_open
 * reported 3 users for the whole property. So nobody knows how many people
 * open the calendar and walk away, or how far they get before they do.
 *
 * Desktop makes this urgent rather than interesting: view_item -> Book Now runs
 * at 21.8% on mobile and 9.4% on desktop. Whether the desktop loss happens
 * before the calendar opens or inside it is the difference between a CTA
 * problem and a booking-flow problem, and right now the data cannot say which.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * It does not touch anything Cowlendar owns. No patched fetch — Cowlendar
 * intercepts window.fetch for /cart/add.js and patching it again would break
 * bookings outright. No synthesized clicks. No injected markup. No dependency
 * on a single Cowlendar class name beyond the outer container, because those
 * are an app's internals and will change without warning.
 *
 * The last change made near this button kept data-modal-trigger pointing at the
 * wrong modal and left the Book Now CTA dead on 50% of desktop workshop pages
 * for a day and a half. This file is read-only for that reason.
 *
 * HOW IT MEASURES DEPTH WITHOUT KNOWING THE INTERNALS
 * ---------------------------------------------------
 * Instead of naming Cowlendar's date cells and time slots, it counts clicks
 * inside the widget between open and close. That is enough to separate the two
 * failure modes that matter:
 *
 *   opened, clicked nothing        -> the calendar itself put them off
 *   picked things, then left       -> no slot worked, or the price/terms did not
 *
 * A class-name-based stepper would read more precisely and break on the next
 * app update. This does not break.
 *
 * WHY THE BUCKETS EXIST
 * ---------------------
 * dwell_ms and click counts are numbers, and a numeric GA4 parameter needs a
 * custom METRIC to be readable, not a custom dimension. dwell_bucket and
 * depth_bucket carry the same information as strings so they work as ordinary
 * report dimensions with no extra registration beyond the two names.
 *
 * EVENTS
 * ------
 *   booking_calendar_open      the picker became visible
 *   booking_calendar_convert   ...and an add_to_cart followed
 *   booking_calendar_abandon   ...and it closed with no add_to_cart
 *
 * open / (open + never-opened) is the CTA's real success rate.
 * abandon / open is the number that has never existed.
 */

(function () {
  'use strict';

  var ROOT_SEL = '.cowlendar-root, #shopify-cowlendar-widget';
  var WIDGET_SEL = '#shopify-cowlendar-widget';

  /* Buttons that can start a booking. Recorded so the funnel can be read per
     entry point — the sticky bar, the hero button and a collection card are
     three different journeys and they do not convert alike. */
  var SOURCE_MAP = [
    ['.mobile_booking_cta', 'sticky_bar'],
    ['.hero-buy-now-btn', 'hero_button'],
    ['[data-hero-buy-now]', 'hero_button'],
    ['.card-collection-product a', 'collection_card'],
    ['.jx-btn a', 'page_button'],
    ['.simple_button', 'page_button']
  ];

  /* ---------------------------------------------------------------- plumbing */

  /* Same buffer-and-flush as header-analytics.js, and for the same measured
     reason: window.gtag is not defined by any inline script on this site, the
     only gtag/js does not start loading until ~718ms, and mirrorGtagFlat drops
     anything sent before it exists. A booking opened in the first second is
     exactly the visitor we least want to lose. */
  var pending = [];
  var polling = false;

  function ready() {
    return typeof window.__kraftGa4Mirror === 'function' && typeof window.gtag === 'function';
  }

  function fire(name, params) {
    try { window.__kraftGa4Mirror(name, params || {}); } catch (e) {}
  }

  function startPolling() {
    if (polling) return;
    polling = true;
    var tries = 0;
    (function poll() {
      if (ready()) {
        var q = pending; pending = []; polling = false;
        for (var i = 0; i < q.length; i++) fire(q[i][0], q[i][1]);
        return;
      }
      if (++tries > 60) { polling = false; pending = []; return; }
      setTimeout(poll, 500);
    })();
  }

  function emit(name, params) {
    if (ready() && !pending.length) { fire(name, params); return; }
    if (pending.length < 30) pending.push([name, params || {}]);
    startPolling();
  }

  function ctx(extra) {
    var out = {
      page_path: window.location.pathname,
      page_category: window.__kraft_page_category || '',
      page_location_city: window.__kraft_page_location_city || '',
      /* The header variant is already in the DOM; carrying it here means the
         booking funnel can be split by which header the visitor arrived under
         without any cross-event stitching. */
      header_variant: (function () {
        var t = document.querySelector('[data-location-toggle]');
        var v = t ? (t.getAttribute('data-current-location') || '').trim().toLowerCase() : '';
        return v || 'global';
      })(),
      booking_surface: (window.innerWidth || 0) >= 992 ? 'desktop' : 'mobile'
    };
    if (extra) Object.keys(extra).forEach(function (k) {
      if (extra[k] !== null && extra[k] !== undefined && extra[k] !== '') out[k] = extra[k];
    });
    return out;
  }

  function dwellBucket(ms) {
    if (ms < 5000) return 'under_5s';
    if (ms < 15000) return '5_15s';
    if (ms < 60000) return '15_60s';
    return 'over_60s';
  }

  function depthBucket(clicks) {
    if (clicks === 0) return 'no_interaction';
    if (clicks <= 2) return 'shallow';
    return 'deep';
  }

  /* ------------------------------------------------------- add_to_cart watch */

  /* Read off dataLayer by index rather than wrapping dataLayer.push. GTM wraps
     push itself and Cowlendar sits on fetch; adding a third wrapper to a chain
     that already carries the booking flow is not worth the risk when a cursor
     over an append-only array answers the same question. */
  var dlCursor = 0;

  function sawAddToCart() {
    var dl = window.dataLayer;
    if (!dl || typeof dl.length !== 'number') return false;
    var found = false;
    for (; dlCursor < dl.length; dlCursor++) {
      var e = dl[dlCursor];
      if (e && (e.event === 'add_to_cart' || e[1] === 'add_to_cart')) found = true;
    }
    return found;
  }

  function resetCursor() {
    var dl = window.dataLayer;
    dlCursor = (dl && typeof dl.length === 'number') ? dl.length : 0;
  }

  /* ------------------------------------------------------------ open / close */

  var lastSource = 'unknown';
  var openAt = 0;
  var clicksInside = 0;
  var isOpen = false;
  var settleTimer = null;

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    /* A click inside the widget is progress through the picker. Counted in
       capture phase because the app stops propagation on its own controls. */
    if (isOpen && t.closest(WIDGET_SEL)) { clicksInside++; return; }

    for (var i = 0; i < SOURCE_MAP.length; i++) {
      if (t.closest(SOURCE_MAP[i][0])) { lastSource = SOURCE_MAP[i][1]; return; }
    }
  }, true);

  function visible(el) {
    if (!el) return false;
    /* offsetParent is null for display:none and for any hidden ancestor, which
       is exactly the condition Cowlendar toggles on .cowlendar-root. */
    return !!(el.offsetParent || el.getClientRects().length);
  }

  function onOpen() {
    if (isOpen) return;
    isOpen = true;
    openAt = Date.now();
    clicksInside = 0;
    resetCursor();
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    emit('booking_calendar_open', ctx({ open_source: lastSource }));
  }

  function onClose() {
    if (!isOpen) return;
    isOpen = false;
    var dwell = Date.now() - openAt;
    var clicks = clicksInside;

    /* Cowlendar closes the picker and THEN the cart request completes, so a
       successful booking looks momentarily identical to an abandonment. Wait
       for the add_to_cart to land before deciding which happened. 4s covers a
       slow /cart/add.js; beyond that it is a genuine abandonment. */
    settleTimer = setTimeout(function () {
      settleTimer = null;
      var converted = sawAddToCart();
      emit(converted ? 'booking_calendar_convert' : 'booking_calendar_abandon', ctx({
        open_source: lastSource,
        dwell_ms: dwell,
        dwell_bucket: dwellBucket(dwell),
        calendar_clicks: clicks,
        depth_bucket: depthBucket(clicks)
      }));
    }, 4000);
  }

  /* --------------------------------------------------------------- observers */

  function watch(root) {
    var wasVisible = visible(root);

    var obs = new MutationObserver(function () {
      var now = visible(root);
      if (now === wasVisible) return;
      wasVisible = now;
      if (now) onOpen(); else onClose();
    });

    /* style and class cover the two ways an app toggles a modal; subtree
       catches Cowlendar replacing the container wholesale on open. */
    obs.observe(root, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
    if (root.parentNode) {
      obs.observe(root.parentNode, { childList: true });
    }
  }

  function init() {
    if (typeof MutationObserver !== 'function') return;

    var root = document.querySelector(ROOT_SEL);
    if (root) { watch(root); return; }

    /* The widget is injected by an app script, so it may not exist yet. Look
       for it for 20 seconds, then stop — most pages have no booking flow at
       all and an unbounded observer on <body> is not worth paying for. */
    var tries = 0;
    (function look() {
      var el = document.querySelector(ROOT_SEL);
      if (el) { watch(el); return; }
      if (++tries > 40) return;
      setTimeout(look, 500);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
